import type { IdentityCurrentStaffSessionResponse, IdentityWorkspaceResponse } from '@exylon/generated-contract-kernel/types';
import { PANEL_DESTINATIONS, WORKSPACE_IDS, type PanelAccessModel, type PanelNavigationRole, type WorkspaceId } from '../foundation/access';
import { IdentityHttpError } from './identity-http';

export type CanonicalStaffSession = IdentityCurrentStaffSessionResponse['result'];
export type RuntimeTenant =
  | { readonly kind: 'restaurant'; readonly id: string; readonly name: string; readonly access: PanelAccessModel }
  | { readonly kind: 'franchise'; readonly id: string; readonly name: string }
  | { readonly kind: 'platform'; readonly id: string; readonly name: 'Exylon' };

export interface PanelRuntimeSession {
  readonly canonical: CanonicalStaffSession;
  readonly tenant: RuntimeTenant;
  readonly personName: string;
  readonly identifier: string;
}

/** An authenticated person awaiting credential rotation has no workspace
 * projection. This identity may render the gate, never the working shell. */
export interface PanelCredentialSession {
  readonly rotationRequired: true;
  readonly canonical: CanonicalStaffSession;
  readonly tenant: { readonly kind: RuntimeTenant['kind']; readonly id: string; readonly name: string };
  readonly personName: string;
  readonly identifier: string;
}

const invalid = (): never => { throw new IdentityHttpError('invalid_response'); };
type NavigationScope = IdentityWorkspaceResponse['result']['result']['navigation_scope'];
/** The server decides a member's navigation from the membership's canonical
 * roles (3.6a); the Panel only names it in its own vocabulary. */
const NAVIGATION_ROLE_BY_SCOPE = {
  management: 'gerencia', service: 'sala', kitchen: 'cocina',
} as const satisfies Readonly<Record<NavigationScope, PanelNavigationRole>>;

/** Identity's two membership envelopes must agree with the hydrated session.
 * A stale/foreign read is never converted to an empty or permissive grant set. */
/** @internal Exported only for its spec. */
export function projectWorkspaceAccess(current: CanonicalStaffSession, response: IdentityWorkspaceResponse): PanelAccessModel {
  if (current.user.rotation_required !== false) return invalid();
  const { result: access, authenticated_identity: identity } = response.result;
  const restaurantId = current.restaurant?.id;
  if (!restaurantId || current.session.membership_tenant_type !== 'restaurant'
    || current.session.membership_tenant_id !== restaurantId
    || access.restaurant_id !== restaurantId || identity.restaurant_id !== restaurantId
    || access.subject_user_id !== current.user.id || identity.subject_user_id !== current.user.id
    || access.employee_id !== current.user.employee_id
    || access.membership_id !== current.user.membership_id || identity.membership_id !== access.membership_id
    || identity.authority !== 'restaurant_member'
    || access.membership_version !== current.session.membership_version
    || identity.auth_version !== access.membership_version
    || current.user.authority_version !== access.membership_version) return invalid();

  const ids = new Set<WorkspaceId>();
  const grants = access.workspace_grants.map((grant) => {
    if (!WORKSPACE_IDS.some((id) => id === grant.workspace_id) || ids.has(grant.workspace_id as WorkspaceId)) return invalid();
    const workspaceId = grant.workspace_id as WorkspaceId;
    ids.add(workspaceId);
    return { workspaceId, provenance: grant.provenance };
  });
  const refs = new Set(access.workspace_refs.map(({ workspace_id }) => workspace_id));
  if (refs.size !== access.workspace_refs.length || refs.size !== ids.size || [...ids].some((id) => !refs.has(id))) return invalid();
  if (access.default_route !== null && !PANEL_DESTINATIONS.some((destination) =>
    destination.workspaceId !== 'CONSTRUCTOR' && ids.has(destination.workspaceId) && destination.path === access.default_route)) return invalid();
  if (!Object.hasOwn(NAVIGATION_ROLE_BY_SCOPE, access.navigation_scope)) return invalid();
  return {
    restaurantId, subjectUserId: current.user.id, grants,
    defaultRoute: access.default_route, exactPermissions: access.exact_permissions,
    // Both navigation facts come from /me/workspaces; the Panel derives
    // nothing from role names. Management navigation is the Panel's only
    // Constructor admission: Home/Board receive it, never re-derive it.
    navigationEnabled: access.navigation_enabled, navigationRole: NAVIGATION_ROLE_BY_SCOPE[access.navigation_scope],
    constructorEnabled: access.navigation_scope === 'management',
  };
}

/** No tenant is selected from a route, token payload, form, or remembered ID. */
export function projectRuntimeSession(current: CanonicalStaffSession, workspaces?: IdentityWorkspaceResponse): PanelRuntimeSession {
  if (current.user.rotation_required !== false) return invalid();
  const { session, restaurant, franchise, user } = current;
  let tenant: RuntimeTenant;
  if (session.membership_tenant_type === 'restaurant') {
    if (!restaurant || franchise !== null || restaurant.id !== session.membership_tenant_id || !workspaces) return invalid();
    tenant = { kind: 'restaurant', id: restaurant.id, name: restaurant.name, access: projectWorkspaceAccess(current, workspaces) };
  } else if (session.membership_tenant_type === 'franchise') {
    if (restaurant !== null || !franchise || !franchise.is_active || franchise.id !== session.membership_tenant_id
      || !user.roles.includes('FRANCHISE_DIRECTOR') || workspaces) return invalid();
    tenant = { kind: 'franchise', id: franchise.id, name: franchise.name };
  } else {
    if (restaurant !== null || franchise !== null || workspaces || !user.roles.includes('SUPERADMIN')) return invalid();
    tenant = { kind: 'platform', id: session.membership_tenant_id, name: 'Exylon' };
  }
  const identifier = user.email?.trim() || user.phone?.trim() || '';
  const personName = [user.name, user.first_surname, user.second_surname].filter(Boolean).join(' ').trim() || identifier;
  if (!identifier || !personName || !Number.isFinite(Date.parse(session.expires_at))) return invalid();
  return { canonical: current, tenant, personName, identifier };
}

/** The restaurant a restaurant session works in, by name: what a screen writes on a card, a period or a sheet. */
export const sessionRestaurantName = (session: Pick<PanelRuntimeSession, 'canonical'>): string => session.canonical.restaurant?.name ?? '';

/** Whose durable work a restaurant session reads and writes. */
export function durableWorkScopeOf(session: Pick<PanelRuntimeSession, 'canonical' | 'tenant'>): Readonly<{ principalId: string; tenantId: string; restaurantId: string }> {
  return { principalId: session.canonical.user.id, tenantId: session.canonical.restaurant?.franchise_id ?? session.tenant.id, restaurantId: session.tenant.id };
}

/** The session's management scope (`navigation_scope: 'management'` from /me/workspaces, a server fact): the one
 * place a binding asks it. An action inside a screen still asks its exact permission or the Backend's verdict. */
export function isManagementAccess(access: Pick<PanelAccessModel, 'navigationRole'>): boolean {
  return access.navigationRole === 'gerencia';
}

export function projectCredentialSession(current: CanonicalStaffSession): PanelCredentialSession {
  const { session, restaurant, franchise, user } = current;
  if (user.rotation_required !== true) return invalid();
  let tenant: PanelCredentialSession['tenant'];
  if (session.membership_tenant_type === 'restaurant') {
    if (!restaurant || franchise !== null || restaurant.id !== session.membership_tenant_id) return invalid();
    tenant = { kind: 'restaurant', id: restaurant.id, name: restaurant.name };
  } else if (session.membership_tenant_type === 'franchise') {
    if (restaurant !== null || !franchise || !franchise.is_active || franchise.id !== session.membership_tenant_id
      || !user.roles.includes('FRANCHISE_DIRECTOR')) return invalid();
    tenant = { kind: 'franchise', id: franchise.id, name: franchise.name };
  } else {
    if (restaurant !== null || franchise !== null || !user.roles.includes('SUPERADMIN')) return invalid();
    tenant = { kind: 'platform', id: session.membership_tenant_id, name: 'Exylon' };
  }
  const identifier = user.email?.trim() || user.phone?.trim() || '';
  const personName = [user.name, user.first_surname, user.second_surname].filter(Boolean).join(' ').trim() || identifier;
  if (!identifier || !personName || !Number.isFinite(Date.parse(session.expires_at))) return invalid();
  return { rotationRequired: true, canonical: current, tenant, personName, identifier };
}
